import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { CheckCircle2, Gift, Clock } from "lucide-react";

export default function Offer() {
  const { industry } = useParams();
  const { location, user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!location) return;
    api.offer(industry, location.country).then(setData).catch(() => setData(false));
  }, [industry, location]);

  if (data === false) return <div className="max-w-2xl mx-auto px-6 py-32 text-center text-slate-500">Offer not found.</div>;
  if (!data) return <div className="max-w-2xl mx-auto px-6 py-32 text-center text-slate-500">Loading…</div>;

  const b = data.bundle;
  const sym = b.symbol;
  const savings = b.regular_price - b.offer_price;

  const claim = () => {
    api.logEvent("offer.clicked", { bundle_slug: b.slug });
    if (!user) return navigate("/login", { state: { from: `/checkout?type=bundle&slug=${b.slug}` } });
    navigate(`/checkout?type=bundle&slug=${b.slug}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="offer-page">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 asf-overline"><Gift className="h-4 w-4" /> Webinar-exclusive offer</div>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter mt-3">{b.title}</h1>
        <p className="text-slate-600 mt-4 max-w-xl mx-auto">{b.description}</p>
      </div>

      <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-asf-orange to-blue-500/40 mt-10">
        <div className="rounded-3xl bg-asf-surface p-8 sm:p-12">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-display text-lg font-semibold mb-4">Everything included</h3>
              <ul className="space-y-3">
                {b.courses.map((c) => (
                  <li key={c.slug} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-asf-orange shrink-0" />
                    <span>{c.title}</span>
                    <span className="ml-auto text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{c.duration_hours}h</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center md:text-right">
              <div className="text-slate-500 line-through text-lg">Regular value {fmtMoney(sym, b.regular_price)}</div>
              <div className="font-display text-6xl font-extrabold text-asf-orange leading-none mt-1">{fmtMoney(sym, b.offer_price)}</div>
              <div className="text-sm text-slate-500 mt-2">You save {fmtMoney(sym, savings)}</div>
              {data.eligible ? (
                <div className="mt-3 inline-flex items-center gap-2 text-xs text-green-400"><CheckCircle2 className="h-4 w-4" /> You're eligible</div>
              ) : (
                <div className="mt-3 text-xs text-slate-500">Register for a webinar to unlock</div>
              )}
              <button data-testid="claim-offer-btn" onClick={claim} className="btn-primary w-full md:w-auto px-10 py-4 mt-6">
                Claim Webinar Offer
              </button>
            </div>
          </div>
        </div>
      </div>

      {!data.eligible && (
        <p className="text-center text-sm text-slate-500 mt-6">
          Not eligible yet? <Link to={`/webinars/${industry}`} className="text-asf-orange font-semibold">Register for the {industry} webinar</Link>
        </p>
      )}
    </div>
  );
}
